import { useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth, peutEcrire } from '../lib/auth'
import { base } from '../lib/db'
import { dateLongue, horodatageCourt, LIBELLE_ORIGINE, LIBELLE_SOURCE } from '../lib/format'
import type { Chantier, Photo, PhotoEnAttente } from '../lib/types'
import { VisionneusePhoto } from '../components/VisionneusePhoto'
import { Superpose } from '../components/Superpose'

type Entree =
  | { genre: 'envoyee'; photo: Photo; url: string | null }
  | { genre: 'attente'; photo: PhotoEnAttente; url: string }

export function FichePhoto({
  entree,
  chantier,
  fermer,
  modifiee,
  position,
  precedente,
  suivante,
  creerDeficience
}: {
  entree: Entree
  chantier: Chantier
  fermer: () => void
  modifiee: () => void
  position?: { rang: number; total: number }
  precedente?: () => void
  suivante?: () => void
  creerDeficience?: (photoClientUuid: string) => void
}) {
  const { profil } = useAuth()
  const [note, setNote] = useState(entree.photo.note ?? '')
  const [occupe, setOccupe] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [pleinEcran, setPleinEcran] = useState(false)
  const ecrivable = peutEcrire(profil, chantier.statut)

  useEffect(() => setNote(entree.photo.note ?? ''), [entree])

  // Flèches du clavier : photo précédente / suivante (sauf pendant la saisie
  // de la note). Échap ferme.
  useEffect(() => {
    function touche(e: KeyboardEvent) {
      const cible = e.target as HTMLElement
      if (cible?.tagName === 'TEXTAREA' || cible?.tagName === 'INPUT') return
      if (e.key === 'ArrowRight' && suivante) suivante()
      else if (e.key === 'ArrowLeft' && precedente) precedente()
      else if (e.key === 'Escape' && !pleinEcran) fermer()
    }
    window.addEventListener('keydown', touche)
    return () => window.removeEventListener('keydown', touche)
  }, [precedente, suivante, fermer, pleinEcran])
  const debutToucher = useRef<{ x: number; y: number } | null>(null)

  async function enregistrer() {
    setOccupe(true)
    setErreur(null)
    try {
      const valeur = note.trim() || null
      if (entree.genre === 'attente') {
        await base.attente.put({ ...entree.photo, note: valeur })
      } else {
        const { error } = await supabase
          .from('photos')
          .update({ note: valeur })
          .eq('id', entree.photo.id)
        if (error) throw error
      }
      modifiee()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e))
      setOccupe(false)
    }
  }

  const p = entree.photo
  const enAttente = entree.genre === 'attente'

  if (pleinEcran && entree.url) {
    return (
      <VisionneusePhoto
        url={entree.url}
        fermer={() => setPleinEcran(false)}
        precedente={precedente}
        suivante={suivante}
        position={position}
      />
    )
  }

  return (
    <Superpose>
      <header className="entete">
        <div className="entete-rang">
          <button className="retour" onClick={fermer}>
            ‹ Photos
          </button>
          <div style={{ flex: 1 }}>
            <h1>Photo</h1>
            <p className="sous">
              {horodatageCourt(p.prise_le)}
              {position ? ` · ${position.rang} / ${position.total}` : ''}
            </p>
          </div>
          <button className="fermer-x" onClick={fermer} aria-label="Fermer la photo" title="Fermer">
            ✕
          </button>
        </div>
      </header>

      <div className="corps" style={{ paddingLeft: 0, paddingRight: 0 }}>
        {entree.url && (
          <div
            className="apercu-zone"
            onTouchStart={(e) => {
              debutToucher.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
            }}
            onTouchEnd={(e) => {
              const d = debutToucher.current
              debutToucher.current = null
              if (!d) return
              const dx = e.changedTouches[0].clientX - d.x
              const dy = e.changedTouches[0].clientY - d.y
              if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
                if (dx < 0) suivante?.()
                else precedente?.()
              }
            }}
          >
            <button
              className="apercu-bouton"
              onClick={() => setPleinEcran(true)}
              aria-label="Agrandir la photo"
            >
              <img className="apercu" src={entree.url} alt="" />
              <span className="apercu-loupe">⤢ Agrandir</span>
            </button>
            {precedente && (
              <button className="fleche-photo gauche" onClick={precedente} aria-label="Photo précédente">
                ‹
              </button>
            )}
            {suivante && (
              <button className="fleche-photo droite" onClick={suivante} aria-label="Photo suivante">
                ›
              </button>
            )}
          </div>
        )}

        <div style={{ padding: '14px 16px 0' }}>
          <div className="sect">Provenance</div>
          <div className="carte bande-nulle">
            <p className="titre">{LIBELLE_SOURCE[p.source] ?? p.source}</p>
            <p className="meta">
              <span>{dateLongue(p.prise_le)}</span>
              <span className="pt" />
              <span>{LIBELLE_ORIGINE[p.origine_date] ?? p.origine_date}</span>
            </p>
            {p.source === 'televersee' && (
              <p className="meta" style={{ lineHeight: 1.5 }}>
                <span>
                  Une photo téléversée ne porte pas la garantie d'heure d'une photo prise dans
                  l'application. La distinction est conservée jusque dans le rapport.
                </span>
              </p>
            )}
            {enAttente && (
              <p className="meta">
                <span>En file d'attente — partira à la reconnexion</span>
              </p>
            )}
          </div>

          <div className="sect">Note</div>
          <label className="champ">
            <span>Optionnelle</span>
            <textarea
              rows={4}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ex. : pare-air posé au mur nord, jonction à reprendre"
              disabled={!ecrivable}
            />
          </label>

          {erreur && <p className="avis erreur">{erreur}</p>}
        </div>
      </div>

      {ecrivable && (
        <div className="actions">
          {creerDeficience && (
            <button className="bouton creux" onClick={() => creerDeficience(p.client_uuid)}>
              Créer une déficience
            </button>
          )}
          <button className="bouton" onClick={() => void enregistrer()} disabled={occupe}>
            {occupe ? 'Enregistrement…' : 'Enregistrer la note'}
          </button>
        </div>
      )}
    </Superpose>
  )
}
